import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { useTranslation } from 'react-i18next';
import { ChatAsset } from '../../models/Chat';
import { isChatImageAsset } from '../../utils/chat';

interface ChatAssetAttachmentProps {
  asset: ChatAsset;
  /** Timeline: large inline preview. List: compact thumbnail in the files tab. */
  variant?: 'timeline' | 'list';
  mt?: number;
}

export function ChatOpenInNewTabLink({ url, compact = false }: { url: string; compact?: boolean }) {
  const { t } = useTranslation();

  return (
    <Link
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      variant="caption"
      onClick={(event) => event.stopPropagation()}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.35,
        flexShrink: 0,
        color: 'primary.light',
        fontSize: compact ? '0.65rem' : undefined,
        whiteSpace: 'nowrap',
      }}
    >
      <OpenInNewRoundedIcon sx={{ fontSize: compact ? 12 : 14 }} />
      {t('chat.files.openInNewTab', 'Open in new tab')}
    </Link>
  );
}

const ChatAssetAttachment: React.FC<ChatAssetAttachmentProps> = ({
  asset,
  variant = 'timeline',
  mt = 0,
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const isImage = isChatImageAsset(asset);

  if (isImage) {
    const previewMaxHeight = variant === 'timeline' ? 360 : 72;
    const previewWidth = variant === 'timeline' ? { xs: '100%', sm: 480, md: 640 } : 72;

    return (
      <>
        <Box sx={{ mt, maxWidth: variant === 'timeline' ? { xs: '100%', sm: 480, md: 640 } : 'none' }}>
          <Box sx={{ position: 'relative', display: 'inline-block', width: variant === 'list' ? previewWidth : '100%' }}>
            <Box
              role="button"
              tabIndex={0}
              onClick={() => setLightboxOpen(true)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setLightboxOpen(true);
                }
              }}
              aria-label={asset.title}
              sx={{
                display: 'block',
                width: variant === 'list' ? previewWidth : '100%',
                borderRadius: 1.5,
                border: 1,
                borderColor: 'divider',
                overflow: 'hidden',
                bgcolor: 'rgba(0,0,0,0.2)',
                cursor: 'zoom-in',
                transition: 'border-color 0.15s ease',
                '&:hover': { borderColor: 'primary.main' },
                '&:focus-visible': {
                  outline: 2,
                  outlineColor: 'primary.main',
                  outlineOffset: 2,
                },
              }}
            >
              <Box
                component="img"
                src={asset.url}
                alt={asset.title}
                loading="lazy"
                sx={{
                  display: 'block',
                  width: '100%',
                  maxHeight: previewMaxHeight,
                  objectFit: 'contain',
                  bgcolor: 'rgba(0,0,0,0.15)',
                }}
              />
            </Box>
          </Box>
          {variant === 'timeline' ? (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
              sx={{ mt: 0.5, px: 0.25, gap: 1 }}
            >
              <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                {asset.title}
              </Typography>
              <ChatOpenInNewTabLink url={asset.url} />
            </Stack>
          ) : null}
        </Box>

        <Dialog
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'rgba(0,0,0,0.92)',
              backgroundImage: 'none',
            },
          }}
        >
          <IconButton
            aria-label="Close"
            onClick={() => setLightboxOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1, color: 'common.white' }}
          >
            <CloseRoundedIcon />
          </IconButton>
          <DialogContent sx={{ p: { xs: 2, sm: 3 }, pt: { xs: 6, sm: 3 } }}>
            <Box
              component="img"
              src={asset.url}
              alt={asset.title}
              sx={{
                display: 'block',
                width: '100%',
                maxHeight: 'calc(100dvh - 96px)',
                objectFit: 'contain',
                mx: 'auto',
              }}
            />
            <Typography variant="body2" color="grey.400" sx={{ mt: 1.5, textAlign: 'center' }}>
              {asset.title}
            </Typography>
            <Box sx={{ mt: 1, textAlign: 'center' }}>
              <ChatOpenInNewTabLink url={asset.url} />
            </Box>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  if (variant === 'list') {
    return null;
  }

  return (
    <Box
      sx={{
        mt,
        maxWidth: { xs: '100%', sm: 560, md: 720 },
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          px: 1.25,
          py: 0.75,
          borderRadius: 1,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'rgba(255,255,255,0.04)',
        }}
      >
        <InsertDriveFileOutlinedIcon sx={{ fontSize: 20, color: 'primary.light' }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Link
            href={asset.url}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            variant="body2"
            sx={{ fontWeight: 600, display: 'block' }}
          >
            {asset.title}
          </Link>
          {asset.description && (
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {asset.description}
            </Typography>
          )}
          <Box sx={{ mt: 0.25 }}>
            <ChatOpenInNewTabLink url={asset.url} compact />
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};

export default ChatAssetAttachment;
